import React, { useMemo } from 'react';
import { BeveledPanel } from '../Equipment/FabProcessTool/BeveledPanel';
import { getStockerMaterials } from './StockerMaterials';
import { StockerExternalLoadPort } from './StockerExternalLoadPort';
import {
  STOCKER_CRANE_X_RATIO,
  STOCKER_IO_TRAY_HEIGHT_RATIO,
  resolveStockerDockCount,
  resolveStockerDockIndexOrFirst,
  resolveStockerDockOffsetsX,
  sortStockerDocks,
} from '../../../../utils/stockerDocks';
import { resolveStockerDimensions, type StockerDimensions } from './stockerTypes';
import { stockerTransferLifterStore, STOCKER_LIFTER_IDLE } from './stockerTransferLifterStore';

export type StockerLoadPortKind =
  | 'interbay-0'
  | 'interbay-1'
  | 'interbay-2'
  | 'interbay-3'
  | 'intrabay-0'
  | 'intrabay-1';

export type StockerIoFace = 'interbay' | 'intrabay';

export type StockerLoadPortBankProps = {
  width: number;
  height: number;
  depth: number;
  dims?: StockerDimensions;
  opacity?: number;
  bayCount?: string | number;
  outsideStocker?: boolean;
  layoutPorts?: LayoutStockerPortRef[];
  occupiedPorts?: Partial<Record<StockerLoadPortKind, boolean>>;
  activePortKind?: StockerLoadPortKind | null;
  isTransferring?: boolean;
  transferProgress01?: number;
  transferOperation?: 'PICKUP' | 'DROP' | 'TRANSFER';
  stockerId?: string;
};


export type LayoutStockerPortRef = {
  id?: string;
  rx?: number;
  ry?: number;
  portIndex?: number;
  name?: string;
};

export function resolveStockerIoPortCounts(opts: {
  bayCount?: string | number;
  outsideStocker?: boolean;
  layoutPortCount?: number;
}): { interbay: number; intrabay: number } {
  return {
    interbay: resolveStockerDockCount({
      layoutPortCount: opts.layoutPortCount ?? 2,
      outsideStocker: opts.outsideStocker,
    }),
    intrabay: 0,
  };
}

function faceKinds(face: StockerIoFace, count: number): StockerLoadPortKind[] {
  const max = face === 'interbay' ? 4 : 2;
  const n = Math.max(0, Math.min(count, max));
  return Array.from({ length: n }, (_, i) => `${face}-${i}` as StockerLoadPortKind);
}

/** −Z = open front / OHT dock face (reversed from previous +Z). */
export const STOCKER_IO_TRAY_FACE_SIGN = -1 as const;
/** External front deck center — outside the glass frame (+Z protrusion). */
export const STOCKER_IO_INTERBAY_Z_RATIO = 0.6;
export const STOCKER_IO_INTRABAY_Z_RATIO = 0.58;
export { STOCKER_IO_TRAY_HEIGHT_RATIO };
export const STOCKER_IO_CRANE_X_RATIO = STOCKER_CRANE_X_RATIO;

/** HMI pose below the load-port row on the +Z (front) dock face. */
export function getStockerLoadPortStatusPanelPose(
  width: number,
  height: number,
  depth: number
): {
  x: number;
  y: number;
  z: number;
  panelW: number;
  panelH: number;
  panelDepth: number;
  faceRotationY: number;
} {
  const resolved = resolveStockerDimensions(width, height, depth);
  const trayY = resolved.loadPortDeckY;
  const loadPortZ = resolved.loadPortZ;
  const panelDepth = depth * 0.028;
  const outward = STOCKER_IO_TRAY_FACE_SIGN;
  const panelH = height * 0.12;
  return {
    x: width * 0.28,
    y: Math.max(height * 0.055, trayY * 0.32),
    z: loadPortZ + outward * panelDepth,
    panelW: width * 0.15,
    panelH,
    panelDepth,
    faceRotationY: outward < 0 ? Math.PI : 0,
  };
}

export function getStockerLoadPortLocalPositions(
  width: number,
  _height: number,
  depth: number,
  opts: {
    bayCount?: string | number;
    outsideStocker?: boolean;
    layoutPorts?: LayoutStockerPortRef[];
  } = {}
): Partial<Record<StockerLoadPortKind, [number, number, number]>> {
  const layoutPorts = sortLayoutPorts(opts.layoutPorts ?? []);
  const counts = resolveStockerIoPortCounts({
    ...opts,
    layoutPortCount: layoutPorts.length || 2,
  });
  const resolved = resolveStockerDimensions(width, _height, depth);
  const y = resolved.loadPortDeckY;
  const interbayZ = resolved.loadPortZ;
  const out: Partial<Record<StockerLoadPortKind, [number, number, number]>> = {};
  const trayX = width * STOCKER_IO_CRANE_X_RATIO;
  const ibKinds = faceKinds('interbay', counts.interbay);
  const ibX = resolveStockerDockOffsetsX(width, layoutPorts, {
    outsideStocker: opts.outsideStocker,
  });

  ibKinds.forEach((kind, i) => {
    out[kind] = [ibX[i] ?? trayX, y, interbayZ];
  });

  return out;
}

export function sortLayoutPorts(ports: LayoutStockerPortRef[]): LayoutStockerPortRef[] {
  return sortStockerDocks(ports) as LayoutStockerPortRef[];
}

export function resolveKitPortForTransfer(
  positions: Partial<Record<StockerLoadPortKind, [number, number, number]>>,
  layoutPorts: LayoutStockerPortRef[],
  transferPortId?: string | null
): StockerLoadPortKind {
  const interbay = (['interbay-0', 'interbay-1', 'interbay-2', 'interbay-3'] as const).filter(
    (k) => positions[k]
  );
  if (interbay.length === 0) {
    const any = Object.keys(positions)[0] as StockerLoadPortKind | undefined;
    return any ?? 'interbay-0';
  }
  const idx = resolveStockerDockIndexOrFirst(layoutPorts, transferPortId, interbay.length);
  return interbay[idx]!;
}

export function getStockerLoadPortFoupPose(
  portBase: [number, number, number],
  stockerHeight: number,
  stockerWidth?: number,
  stockerDepth?: number
): [number, number, number] {
  if (stockerWidth != null && stockerDepth != null) {
    const dims = resolveStockerDimensions(stockerWidth, stockerHeight, stockerDepth);
    return [portBase[0], dims.foupSeatY, portBase[2]];
  }
  return [portBase[0], stockerHeight * STOCKER_IO_TRAY_HEIGHT_RATIO + stockerHeight * 0.018, portBase[2]];
}

export function portFaceOf(kind: StockerLoadPortKind): StockerIoFace {
  return kind.startsWith('intrabay') ? 'intrabay' : 'interbay';
}

export function primaryInterbayPort(
  positions: Partial<Record<StockerLoadPortKind, [number, number, number]>>
): StockerLoadPortKind | null {
  for (const k of ['interbay-0', 'interbay-1', 'interbay-2', 'interbay-3'] as const) {
    if (positions[k]) return k;
  }
  return null;
}

export function primaryIntrabayPort(
  positions: Partial<Record<StockerLoadPortKind, [number, number, number]>>
): StockerLoadPortKind | null {
  for (const k of ['intrabay-0', 'intrabay-1'] as const) {
    if (positions[k]) return k;
  }
  return null;
}

function dockFootprint(width: number, depth: number): { cabW: number; cabD: number } {
  return {
    cabW: Math.min(width * 0.28, Math.max(depth * 0.4, 1.75)),
    cabD: Math.max(depth * 0.32, 1.65),
  };
}

/**
 * Stocker I/O load ports on the front (+Z) tray face — flat deck at bottom center.
 */
export const StockerLoadPortBank: React.FC<StockerLoadPortBankProps> = ({
  width,
  height,
  depth,
  dims: dimsProp,
  opacity = 1,
  bayCount,
  outsideStocker = false,
  layoutPorts,
  occupiedPorts: occupiedPortsProp,
  activePortKind: activePortKindProp,
  isTransferring: isTransferringProp,
  transferProgress01: transferProgress01Prop,
  transferOperation: transferOperationProp,
  stockerId,
}) => {
  const [storeState, setStoreState] = React.useState(() =>
    stockerId ? stockerTransferLifterStore.get(stockerId) : STOCKER_LIFTER_IDLE
  );

  React.useEffect(() => {
    if (!stockerId) return;
    setStoreState(stockerTransferLifterStore.get(stockerId));
    return stockerTransferLifterStore.subscribe(() => {
      setStoreState(stockerTransferLifterStore.get(stockerId));
    });
  }, [stockerId]);

  const occupiedPorts = occupiedPortsProp ?? (stockerId ? storeState.occupiedPorts : {});
  const activePortKind = activePortKindProp ?? (stockerId ? storeState.activePortKind : null);
  const isTransferring = isTransferringProp ?? (stockerId ? storeState.isTransferring : false);
  const transferProgress01 = transferProgress01Prop ?? (stockerId ? storeState.transferProgress01 : 0);
  const transferOperation = transferOperationProp ?? (stockerId ? storeState.transferOperation : 'PICKUP');

  const dims = useMemo(
    () => dimsProp ?? resolveStockerDimensions(width, height, depth),
    [dimsProp, width, height, depth]
  );
  const positions = useMemo(
    () =>
      getStockerLoadPortLocalPositions(width, height, depth, {
        bayCount,
        outsideStocker,
        layoutPorts,
      }),
    [width, height, depth, bayCount, outsideStocker, layoutPorts]
  );
  const kinds = Object.keys(positions) as StockerLoadPortKind[];

  const { cabW: dockW, cabD: dockD } = dockFootprint(width, depth);
  const mats = useMemo(() => getStockerMaterials(opacity), [opacity]);
  const deckPadH = Math.max(0.05, dims.loadPortDeckY * 0.048);
  const deckSpanZ = Math.abs(dims.frontFaceZ - dims.loadPortDeckFrontZ) + depth * 0.04;
  const deckCenterZ = (dims.frontFaceZ + dims.loadPortDeckFrontZ) * 0.5;
  const portList = useMemo(
    () =>
      kinds
        .map((kind) => {
          const position = positions[kind];
          if (!position) return null;
          return { kind, x: position[0], z: position[2] };
        })
        .filter((p): p is { kind: StockerLoadPortKind; x: number; z: number } => p != null),
    [kinds, positions]
  );

  return (
    <group name="StockerLoadPortBank">
      {/* Extended OHT drop deck — spans frame front to outside load-port pads */}
      <BeveledPanel
        size={[width * 0.9, deckPadH, deckSpanZ]}
        material={mats.whiteMetal}
        position={[0, dims.loadPortDeckY - deckPadH * 0.45, deckCenterZ]}
        radius={Math.min(width, depth) * 0.008}
        smoothness={3}
        castShadow
        receiveShadow
      />
      <BeveledPanel
        size={[width * 0.88, deckPadH * 0.35, deckSpanZ * 0.98]}
        material={mats.brushedMetal}
        position={[0, dims.loadPortDeckY - deckPadH * 0.12, deckCenterZ]}
        radius={Math.min(width, depth) * 0.006}
      />
      <StockerExternalLoadPort
        dims={dims}
        ports={portList}
        dockW={dockW}
        dockD={dockD}
        occupiedPorts={occupiedPorts}
        activePortKind={activePortKind}
        isTransferring={isTransferring}
        transferProgress01={transferProgress01}
        transferOperation={transferOperation}
        lifterFoupPresent={stockerId ? storeState.foupPresent : false}
        opacity={opacity}
      />
    </group>
  );
};

export default StockerLoadPortBank;

